import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import WithWizard from 'modules/views/shared/Wizard/WithWizard';
import NavigationButtons from 'modules/views/shared/NavigationButtons';
import {
  CertificationWarningModal,
  CertificationWarningBanner,
  DeleteCertificationModal,
  AddCertificationModal,
  CertificatesGrid,
  Certificate,
} from './components';

import { useWizardStepNavigation } from 'modules/views/shared/Wizard';
import { useCertifications, useCertificationModal, useCertificationOperations, useSubmission } from './hooks';

import { useStyles } from './styles';

const Certifications: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { handlePreviousStep, canGoToPreviousStep } = useWizardStepNavigation();

  // Data and modal state
  const {
    allCertifications,
    certificationGroups,
    typeOptions,
    isLoading: isLoadingData,
    isArchivedProject,
  } = useCertifications();
  const { modalState, openAddModal, openEditModal, closeModal, setEditData, setLoading } = useCertificationModal();

  // Delete state
  const [deleteCertification, setDeleteCertification] = useState<Certificate | null>(null);

  // Submission logic
  const submissionProps = {
    certificationGroups: certificationGroups || [],
    allCertifications: allCertifications || [],
    isArchivedProject,
  };
  const {
    isSubmitting,
    showWarningModal,
    isViewMoreMode,
    handleSubmit,
    handleOpenWarningModal,
    handleWarningModalClose,
    handleProceedAnyway,
    getRequiredGroups,
  } = useSubmission(submissionProps);

  // Get required groups for marquee display
  const requiredGroups = getRequiredGroups();

  // Operations
  const { saveCertification, loadCertificationDetails } = useCertificationOperations(
    typeOptions,
    modalState,
    setEditData,
    setLoading,
    closeModal,
  );

  // Event handlers
  const handleEditCertificate = async (certificateId: string) => {
    openEditModal(certificateId);
    try {
      await loadCertificationDetails(certificateId);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleSaveCertification = async (certificationData: any) => {
    await saveCertification(certificationData);
  };

  const handleDeleteCertificate = (certificate: Certificate) => {
    setDeleteCertification(certificate);
  };

  const handleCloseDeleteModal = () => {
    setDeleteCertification(null);
  };

  const content = (
    <div className={classes.root}>
      <ContentHeader title={t('certifications.title')} subtitle={t('certifications.subtitle')} />
      {/* Show warning banner if there are required certifications missing and not archived */}
      {!isArchivedProject && requiredGroups.length > 0 && (
        <CertificationWarningBanner requiredGroups={requiredGroups} onViewMore={handleOpenWarningModal} />
      )}
      <CertificatesGrid
        certificates={allCertifications || []}
        isArchivedProject={isArchivedProject}
        onAddCertificate={openAddModal}
        onEditCertificate={handleEditCertificate}
        onDeleteCertificate={handleDeleteCertificate}
      />
      <NavigationButtons
        secondaryButtonProps={{
          isHidden: !canGoToPreviousStep,
          onClick: handlePreviousStep,
          testId: 'certifications-back-button',
        }}
        primaryButtonProps={{
          label: t('certifications.next'),
          isLoading: isSubmitting,
          hasEndArrow: true,
          testId: 'certifications-next-button',
          onClick: handleSubmit,
        }}
      />
      {/* Modals */}
      <CertificationWarningModal
        isVisible={showWarningModal}
        certificationGroups={certificationGroups || []}
        allCertifications={allCertifications || []}
        onClose={handleWarningModalClose}
        onCancel={handleWarningModalClose}
        onProceedAnyway={handleProceedAnyway}
        isViewMoreMode={isViewMoreMode}
        onAddCertificate={openAddModal}
      />
      <AddCertificationModal
        isVisible={modalState.isOpen}
        onClose={closeModal}
        onSave={handleSaveCertification}
        typeOptions={typeOptions}
        editData={modalState.editData}
        isEditMode={modalState.mode === 'edit'}
        isLoadingEditDetails={modalState.isLoadingDetails}
        certificationGroups={certificationGroups || []}
        showGroupSelection={false}
      />
      <DeleteCertificationModal
        isVisible={!!deleteCertification?.id}
        certificate={deleteCertification}
        onClose={handleCloseDeleteModal}
        onDeleteSuccess={handleCloseDeleteModal}
      />
    </div>
  );

  return <WithWizard isLoading={isLoadingData} disableNavigation={isSubmitting} content={content} />;
};

export default Certifications;
